/**
 * The tenant's OFFERS — one read, two uses.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS ITS OWN FILE
 * ---------------------------------------------------------------------------
 * The dashboard needs the offers twice, and the two uses need different rights:
 *
 *   - the OFFERS WIDGET counts them by state — that needs `catalog.offer.read`;
 *   - the STOCK WIDGET names its low-stock lines by SKU — and a stock line is keyed by the
 *     OFFER id (M18), so the name comes from the offer (M13). That is the SAME read.
 *
 * So the read lives here, once, and both callers ask this module for it. The alternative was
 * two copies of the same request in two files, and two copies of a contract drift.
 *
 * ---------------------------------------------------------------------------
 * A REFUSAL IS NOT A FAULT
 * ---------------------------------------------------------------------------
 * The two uses do not have the same entitlement. An actor may hold `inventory.stock.read` and
 * not `catalog.offer.read` — a platform administrator sees the stock of a tenant they cannot
 * sell for. So the stock lines must survive a refused offer read: the figures are still true,
 * they simply carry no name.
 *
 * That is why only a REFUSAL is tolerated here. Every other failure THROWS — a broken read is
 * a fault the reader must see, not a name that quietly goes missing.
 */
import { ApiError } from '@/api/client/problem';
import { http } from '@/api/client/http';
import { OfferListResponseSchema, type Offer } from '@/api/schemas/catalog';

/** The tenant's own sellable rows, exactly as the API sent them. */
export async function readOffers(): Promise<readonly Offer[]> {
  const response = await http.get('/catalog/offers', OfferListResponseSchema);
  return response.data;
}

/**
 * The offer index — offer id to the SKU the tenant sells it under.
 *
 * The SKU is a fact about the offer, so it can only be read by someone who may read the
 * offers. A refusal therefore yields an EMPTY index, not an error: the caller still has every
 * stock line, and each one simply carries no SKU.
 */
export async function offerSkus(): Promise<ReadonlyMap<string, string>> {
  try {
    const offers = await readOffers();
    return new Map(offers.map((offer) => [offer.id, offer.sku]));
  } catch (error) {
    if (error instanceof ApiError && error.status === 403) return new Map();
    throw error;
  }
}
